<template>
  <div style="width: 100%">
    <q-input
      v-model="searchText"
      :dense="dense"
      :placeholder="placeholder"
      :class="{ 'dense-width': dense }"
      :loading="loading"
      flat
      filled
      clearable
      @focus="openMenu = true"
      @keydown.down="focusNext(0)"
      @keydown.tab.prevent="focusNext(0)"
    />
    <q-menu
      id="market-autocomplete-menu"
      ref="market-autocomplete-menu"
      v-model="showModal"
      no-focus
      no-parent-event
      max-width="350px"
      transition-duration="0"
      class="q-pa-sm q-gutter-y-xs"
      tabindex="1"
    >
      <template v-if="options.length > 0">
        <button
          v-for="(option, idx) in options"
          :id="`option-${idx}`"
          :key="`${option.name}-${option.city}-${option.state}`"
          :tabindex="idx + 1"
          class="option-section q-pa-sm no-button full-width"
          @click="rowClicked(option)"
          @keydown.down="focusNext(idx === options.length - 1 ? 0 : idx + 1)"
          @keydown.up="focusNext(idx === 0 ? options.length - 1 : idx - 1)"
        >
          <div class="building-name">
            {{ option.name }}
          </div>
          <div class="row q-gutter-x-xs q-mt-xs">
            <q-badge
              v-for="tag in [option.city, option.state, ...option.tags].filter((_) => _)"
              :key="tag!"
              class="extras"
              outline
            >
              {{ tag }}
            </q-badge>
          </div>
        </button>
      </template>
      <template v-else>
        <div>No results found</div>
      </template>
    </q-menu>
  </div>
</template>

<script lang="ts">
import type { MarketSearchResponse } from 'common'
import { useRentsourceStore } from '@/stores/rentsource'
import type { QMenu } from 'quasar'

export default {
  name: 'MarketAutocomplete',
  components: {},
  props: {
    placeholder: {
      type: String,
      default: 'Enter a neighborhood, msa, city or zip code'
    },
    dense: {
      type: Boolean,
      default: false
    },
    lat: {
      type: Number,
      default: null
    },
    lon: {
      type: Number,
      default: null
    },
    showIcon: {
      type: Boolean,
      default: true
    }
  },
  emits: ['new-market'],
  data() {
    return {
      options: [] as MarketSearchResponse[],
      loading: false,
      searchText: null as string | null,
      openMenu: false
    }
  },
  computed: {
    showModal: {
      get() {
        if (this.options.length <= 0) {
          return false
        }
        if (this.searchText === null || this.searchText.length < 3) {
          return false
        }
        return this.openMenu
      },
      set(val: boolean) {
        this.openMenu = val
      }
    }
  },
  watch: {
    searchText(val) {
      if (val === null || val.length < 3) {
        this.options = []
      }
      this.search(val)
    }
  },
  methods: {
    focusNext(idx: number) {
      document.getElementById(`option-${idx}`)?.focus()
    },
    search(val: string | number | null) {
      if (typeof val === 'number') {
        return
      }
      if (val && val.length > 3) {
        this.loading = true
        useRentsourceStore()
          .marketShapeSearch(val)
          .then((options) => {
            this.options = options
            // on next tick, we get the id market-autocomplete-menu and call updatePosition() to reposition the menu
            this.$nextTick(() => {
              const menu = this.$refs['market-autocomplete-menu'] as QMenu | undefined
              if (menu) {
                menu.updatePosition()
              }
            })
          })
          .finally(() => {
            this.loading = false
          })
      } else if (val === null) {
        this.options = []
      }
    },
    rowClicked(row: MarketSearchResponse) {
      this.$emit('new-market', row)
      this.options = []
      this.searchText = null
      this.loading = false
    }
  }
}
</script>
<style lang="scss" scoped>
@use 'sass:color';
.dense-width {
  max-width: 200px;
}

.option-section {
  border-radius: 4px;

  .building-name {
    font-size: 14px;
    font-weight: 500;
  }

  .extras {
    font-size: 12px;
  }
}

.body--dark {
  .option-section {
    .extras {
      color: white;
    }

    .market-tag {
      color: white;
    }

    &:hover {
      background: linear-gradient($primary, color.scale($primary, $lightness: -5%));
      color: white;

      .extras {
        color: white;
      }

      .market-tag {
        border: 1px solid $grey-3;
        background-color: $grey-2;
        color: $grey-9;
      }
    }
  }
}

.body--light {
  .option-section {
    .extras {
      color: $grey-9;
    }

    &:hover {
      color: $primary;
      background-color: $grey-1;

      .extras {
        color: $primary;
      }

      .market-tag {
        border: 1px solid $primary;
        color: $primary;
      }
    }
  }
}

.market-tag {
  display: inline-block;
  margin-right: 5px;
  padding: 2px 5px;
  border-radius: 4px;
  border: 1px solid $grey-5;
  color: $grey-9;
  font-size: small;
  margin-top: 2px;
}

.no-button {
  background: none;
  color: inherit;
  border: none;
  padding: inherit;
  font: inherit;
  cursor: pointer;
  text-align: left;
}
</style>
